import { useState } from 'react'
import { com } from '@eidu/form'
import { Box, Button, Checkbox, FormControlLabel, Popover, Stack, Typography } from '@mui/material'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import EntityFormInput from '../input/EntityFormInput'
import useEntityById from '../../io/useEntityById'
import Field = com.eidu.sharedlib.entity.field.Field
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull

const NULL_FILTER_VALUE = 'null'

export type FilterPopoverProps = {
  field: Field
  currentValue: string | null
  onApply: (value: string | null, label?: string) => void
  onClear: () => void
  anchorEl: HTMLElement | null
  onClose: () => void
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const FilterPopover = ({
  field,
  currentValue,
  onApply,
  onClear,
  anchorEl,
  onClose,
  entityTypes,
}: FilterPopoverProps) => {
  const initialValue = currentValue === NULL_FILTER_VALUE ? '' : currentValue ?? ''
  const [fieldValue, setFieldValue] = useState<DraftFieldValue>({
    fieldId: field.id,
    type: field.type,
    value: initialValue,
  })
  const [isNull, setIsNull] = useState(currentValue === NULL_FILTER_VALUE)

  const selectedEntityId =
    field.type === FieldType.Reference ? entityIdFromStringOrNull(fieldValue.value) ?? undefined : undefined
  const { entity: selectedEntity } = useEntityById(selectedEntityId)

  const handleApply = () => {
    if (isNull) {
      onApply(NULL_FILTER_VALUE)
    } else if (fieldValue.value.trim()) {
      const label = selectedEntity?.primaryLabelText
      onApply(fieldValue.value.trim(), label)
    } else {
      onClear()
    }
    onClose()
  }

  const handleClear = () => {
    onClear()
    onClose()
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box sx={{ p: 2, minWidth: 280, maxWidth: 400 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            Filter: {field.name}
          </Typography>

          <EntityFormInput
            field={field}
            fieldValue={fieldValue}
            entityTypes={entityTypes}
            updateField={setFieldValue}
            disabled={isNull}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isNull}
                onChange={(e) => {
                  setIsNull(e.target.checked)
                  if (e.target.checked) {
                    setFieldValue({ ...fieldValue, value: '' })
                  }
                }}
                size="small"
              />
            }
            label="Empty"
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleClear}>
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  )
}

export { NULL_FILTER_VALUE }
export default FilterPopover
